import {$data} from "../helpers/data";
import {$events} from "../helpers/events";
import {$dom} from "../helpers/dom";
import {$style} from "../helpers/style";
import variables from "../variables";
import is from "is_js";
import {createElementFromHTML, getRandomInt, isElement, loop, preventDefault, warn} from '../helpers/_utilities';
import {$ui} from '../helpers/ui';

const {merge} = $data;

const {
	exist,
	get,
	attr,
	append,
	remove,
	createElement,
	addClass
} = $dom;

export default class ModalController {
	constructor(options = {}) {

		this.defaults = {
			modalsSelector: '.page__modal',
			openTrigger: 'data-modal-open',
			closeTrigger: 'data-modal-close',
			activeClass: 'is-active',
			overlap: false,
			notifyTemplateId: 'template--modal-notify',
			notifyModalSelector: '.js-notify-modal'
		};

		this.options = merge(this.defaults, options);
		this.activeModal = null;
		this.opened = [];
		this.overlap = this.options.overlap;
		this.notifyTemplate = null;
		this.openSelector = `[${this.options.openTrigger}]`;
		this.closeSelector = `[${this.options.closeTrigger}]`;
		this.modalDynamicIds = [];

		this._show = this._show.bind(this);
		this._hide = this._hide.bind(this);
		this._closeByEsc = this._closeByEsc.bind(this);
		this.handleNotifyClosed = this.handleNotifyClosed.bind(this);
	}

	_checkChangeStateCallback(cbObj, callbackName, payload) {
		if (is.object(cbObj) && is.function(cbObj[callbackName])) cbObj[callbackName](payload);
	}

	_change(action, modal, callbacks) {
		switch (action) {
			case 'show':
				$dom.addClass(modal, this.options.activeClass);
				$style.set(modal, 'display', 'block');
				$dom.attr(modal, 'aria-hidden', 'false');

				this.opened.push(modal);

				this._checkChangeStateCallback(callbacks, 'onStart', modal);
				$events
					.emit(variables.customEventNames.modal.open, modal, {modal})
					.add('animationend', modal, () => {
						$events.emit(variables.customEventNames.modal.opened, modal, {modal});
						this._checkChangeStateCallback(callbacks, 'onEnd', modal);
						if (is.touchDevice()) $ui.blockScroll('enable')
					}, {
						once: true
					})
				;
				break;

			case 'hide':

				$dom.attr(modal, 'aria-hidden', 'true');

				this._checkChangeStateCallback(callbacks, 'onStart', modal);

				$events
					.emit(variables.customEventNames.modal.close, modal, {modal})
					.add('animationend', modal, () => {
						$dom.removeClass(modal, this.options.activeClass);
						$style.set(modal, 'display', 'none');
						$events.emit(variables.customEventNames.modal.closed, modal, {modal});
						this._checkChangeStateCallback(callbacks, 'onEnd', modal);
						if (is.touchDevice()) $ui.blockScroll('disable')
					}, {
						once: true
					});

				this.opened = this.opened.filter(item => item !== modal);

				break;
		}
	}

	_show(e) {
		const link = e.target.closest(this.openSelector);
		if (isElement(link)) this.open(attr(link, this.options.openTrigger));
	}

	_hide(e) {
		preventDefault(e);
		this.close()
	}

	_closeByEsc(e) {
		if (this.opened.length > 0 && e.key.toLowerCase().includes('esc')) this.close();
	}

	get lastModalDynamicId() {
		return this.modalDynamicIds[this.modalDynamicIds.length - 1] || null
	}

	handleNotifyClosed() {
		const ind = this.modalDynamicIds.indexOf(this.lastModalDynamicId);

		remove('#'+this.lastModalDynamicId);
		this.modalDynamicIds.splice(ind, 1);
	}

	open(modalId, callbacks = {}) {

		this.activeModal = $dom.get('#' + modalId);

		if (this.opened.length > 0 && !this.overlap) this._change('hide', this.opened[0], callbacks);

		this._change('show', this.activeModal, callbacks);

		if (this.opened.length > 1 && this.overlap) {
			let activeModals = $dom.getAll(this.options.modalsSelector).filter(modal => {
				return $dom.hasClass(modal, this.options.activeClass) && modal !== this.activeModal
			});
			$dom.insertAfter(this.activeModal, activeModals[activeModals.length - 1]);
		}

	}

	close(callbacks) {

		if (this.opened.length > 0) {

			if (this.opened.length > 1) {
				this.activeModal = this.opened[this.opened.length - 1]
			} else if (this.opened.length === 1) {
				this.activeModal = this.opened[0];
			}

			this._change('hide', this.activeModal, callbacks);

		}

	}

	notify(opts) {

		if (is.string(this.notifyTemplate)) {

			const defaults = {
				title: '',
				messages: [],
				classes: null,
				buttons: []
			};

			const
				options =	merge(defaults, opts)
			;

			let temp = this.notifyTemplate;

			const
				{title, messages, buttons, classes} = options,
				markup = createElementFromHTML(temp),
				titleOutput = get('.js-notify-modal-title', markup),
				messagesOutput = get('.js-notify-modal-messages', markup),
				buttonsOutput = get('.js-notify-modal-buttons', markup)
			;

			if (is.string(title) && isElement(titleOutput) && title !== '') {
				titleOutput.innerText = title
			} else {
				remove(titleOutput)
			}

			if (messages.length) {

				loop(messages, message => {

					const
						li = createElement('li'),
						p = createElement('p');

					p.innerHTML = message;
					li.append(p);

					append(messagesOutput, li);

				})
			} else {
				remove(messagesOutput)
			}

			if (buttons.length) {

				loop(buttons, (btn, index) => {
					const
						{content, tag, classes, modalClose} = btn,
						buttonContent = createElement('span', {class: 'button__content'})
					;

					let button, btnOpts, btnOptsCmn = {
						class: classes
					};

					switch (tag) {
						case 'a':
							btnOpts = Object.assign(btnOptsCmn, {
								href: btn.href || '#'
							});
							break;

						case 'button':
							btnOpts = Object.assign(btnOptsCmn, {
								type: btn.type || 'button'
							});
							break;
					}

					button = createElement(tag, btnOpts);

					if (modalClose) attr(button, 'data-modal-close', '');

					buttonContent.innerText = content;
					append(button, buttonContent);
					append(buttonsOutput, button);
				})

			} else {
				remove(buttonsOutput)
			}

			this.modalDynamicIds.push('notify-modal-id--'+getRandomInt(0, 999).toString());


			attr(markup, 'id', this.lastModalDynamicId);
			append(document.body, markup);

			if (classes) addClass(markup, classes);

			this.open(this.lastModalDynamicId);
			return markup

		} else {
			warn('Please! Insert the template of notify modal into the HTML-markup', 'ModalController, notify method')
		}

	}

	listeners(action) {

		switch (action) {
			case 'add':
				$events.delegate
					.on('click tap', this.openSelector, this._show)
					.on('click tap',  this.closeSelector, this._hide)
					.on('keyup', document, this._closeByEsc)
					.on(variables.customEventNames.modal.closed, this.options.notifyModalSelector, this.handleNotifyClosed)
				;
				break;
			case 'remove':
				$events.delegate
					.off('click tap', this.openSelector, this._show)
					.off('click tap',  this.closeSelector, this._hide)
					.off('keyup', document, this._closeByEsc)
					.off(variables.customEventNames.modal.closed, this.options.notifyModalSelector, this.handleNotifyClosed)
				;
				break;
		}
	}

	init() {
		this.listeners('add');

		let tSelector = '#'+this.options.notifyTemplateId;
		if (exist(tSelector)) this.notifyTemplate =	get(tSelector).innerHTML.trim();

	}

	destroy() {
		this.listeners('remove');
		this.notifyTemplate = null;
	}


}
